/**
 * sentry.config.js
 * 
 * Configuration centralisée pour Sentry error tracking
 * 
 * Documentation: https://docs.sentry.io/platforms/javascript/guides/react/
 */

import * as Sentry from '@sentry/react';
import APP_CONFIG from './app.config';

/**
 * Configuration Sentry par environnement
 */
const SENTRY_CONFIG = {
  development: {
    enabled: false, // Désactivé en dev pour ne pas polluer
    environment: 'development',
    tracesSampleRate: 1.0, // 100% des transactions en dev
    replaysSessionSampleRate: 0.1, // 10% des sessions
    replaysOnErrorSampleRate: 1.0, // 100% en cas d'erreur
  },
  staging: {
    enabled: true,
    environment: 'staging',
    tracesSampleRate: 0.5, // 50% des transactions
    replaysSessionSampleRate: 0.1, // 10% des sessions
    replaysOnErrorSampleRate: 1.0, // 100% en cas d'erreur
  },
  production: {
    enabled: true,
    environment: 'production',
    tracesSampleRate: 0.1, // 10% des transactions (économique)
    replaysSessionSampleRate: 0.01, // 1% des sessions
    replaysOnErrorSampleRate: 1.0, // 100% en cas d'erreur
  }
};

/**
 * Initialise Sentry
 * @param {string} environment - Environnement (development, staging, production)
 */
export function initSentry(environment = 'development') {
  const config = SENTRY_CONFIG[environment] || SENTRY_CONFIG.development;
  
  // Si Sentry est désactivé pour cet environnement, ne rien faire
  if (!config.enabled) {
    console.log('🔕 Sentry disabled for environment:', environment);
    return;
  }
  
  // Récupérer le DSN depuis les variables d'environnement
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    console.warn('⚠️ Sentry DSN not configured, error tracking disabled');
    return;
  }
  
  try {
    Sentry.init({
      dsn,
      
      // Informations de l'application
      release: `${APP_CONFIG.shortName}@${APP_CONFIG.version}`,
      environment: config.environment,
      
      // Intégrations React Router
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true, // Masquer le texte pour RGPD
          blockAllMedia: true, // Bloquer les médias pour RGPD
        }),
      ],
      
      // Performance Monitoring
      tracesSampleRate: config.tracesSampleRate,
      
      // Session Replay
      replaysSessionSampleRate: config.replaysSessionSampleRate,
      replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
      
      // Filtrage des erreurs
      beforeSend(event, hint) {
        // Ne pas envoyer les erreurs de développement
        if (config.environment === 'development') {
          console.log('Sentry event (dev):', event);
          return null; // Bloquer en dev
        }
        
        // Filtrer les erreurs non pertinentes
        const error = hint.originalException;
        
        // Ignorer les erreurs de réseau temporaires
        if (error?.message?.includes('Failed to fetch')) {
          return null;
        }
        
        // Ignorer les erreurs d'extensions navigateur
        if (error?.stack?.includes('chrome-extension://')) {
          return null;
        }
        
        // Ignorer les erreurs de scripts externes
        if (error?.stack?.includes('https://www.googletagmanager.com')) {
          return null;
        }
        
        return event;
      },
      
      // Ignorer certaines erreurs par pattern
      ignoreErrors: [
        // Erreurs du navigateur
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
        // Erreurs d'extensions
        /chrome-extension/i,
        /moz-extension/i,
        // Erreurs réseau
        'NetworkError',
        'Network request failed',
      ],
      
      // URLs à ne pas tracker
      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
      ],
    });
    
    console.log('✅ Sentry initialized for environment:', config.environment);
    
    // Définir le contexte utilisateur par défaut
    Sentry.setContext('app', {
      name: APP_CONFIG.shortName,
      version: APP_CONFIG.version,
      environment: config.environment,
    });
    
  } catch (error) {
    console.error('❌ Failed to initialize Sentry:', error);
  }
}

/**
 * Définir l'utilisateur courant dans Sentry
 * @param {Object} user - Informations utilisateur
 */
export function setSentryUser(user) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  
  // Ne pas envoyer d'informations sensibles
  Sentry.setUser({
    id: user.uid,
    email: user.email,
    role: user.role,
  });
}

/**
 * Capturer une exception manuellement
 * @param {Error} error - L'erreur à capturer
 * @param {Object} context - Contexte additionnel
 */
export function captureException(error, context = {}) {
  Sentry.withScope((scope) => {
    // Ajouter le contexte
    Object.keys(context).forEach(key => {
      scope.setContext(key, context[key]);
    });
    
    // Capturer l'erreur
    Sentry.captureException(error);
  });
}

/**
 * Capturer un message personnalisé
 * @param {string} message - Le message
 * @param {string} level - Niveau (info, warning, error)
 * @param {Object} context - Contexte additionnel
 */
export function captureMessage(message, level = 'info', context = {}) {
  Sentry.withScope((scope) => {
    // Ajouter le contexte
    Object.keys(context).forEach(key => {
      scope.setContext(key, context[key]);
    });
    
    // Capturer le message
    Sentry.captureMessage(message, level);
  });
}

/**
 * Ajouter un breadcrumb (fil d'ariane)
 * @param {string} message - Message du breadcrumb
 * @param {string} category - Catégorie (navigation, http, user, etc.)
 * @param {Object} data - Données additionnelles
 */
export function addBreadcrumb(message, category = 'default', data = {}) {
  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data,
  });
}

/**
 * Démarrer une span de performance compatible avec les versions récentes de Sentry
 * @param {string} name - Nom de la transaction
 * @param {string} op - Type d'opération
 * @returns {object|null} Span Sentry
 */
export function startTransaction(name, op = 'custom') {
  if (typeof Sentry.startInactiveSpan !== 'function') {
    return null;
  }

  return Sentry.startInactiveSpan({
    name,
    op,
    forceTransaction: true,
  });
}

export default Sentry;
